import { useCallback, useEffect, useMemo, useState } from 'react';
import { BASE_URL } from '../../../utils/config';
import { useAuthStore } from '../../../utils/stores/useAuthStore';
import { useUserStore } from '../../../utils/stores/useUserStore';

export interface ExperimentalUser {
  id: string;
  email: string;
  feature_type: string;
  added_by: string;
  notes?: string;
  experiment_metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ExperimentalAccess {
  isVenereUser: boolean;
  isAdminUser: boolean;
  hasAnyAccess: boolean;
  featureTypes: string[];
  lastChecked: Date | null;
  userData?: ExperimentalUser;
}

export const useExperimentalAccess = () => {
  const { userData } = useUserStore();
  const { auth } = useAuthStore();
  
  const [access, setAccess] = useState<ExperimentalAccess>({
    isVenereUser: false,
    isAdminUser: false,
    hasAnyAccess: false,
    featureTypes: [],
    lastChecked: null,
    userData: undefined,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get experimental user by email with timeout protection
  const getExperimentalUserByEmail = useCallback(async (email: string): Promise<ExperimentalUser | null> => {
    try {
      // Get auth token if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (auth.idToken) {
        headers['Authorization'] = `Bearer ${auth.idToken}`;
      }

      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 5000); // 5 second timeout
      });

      // Create the fetch promise
      const fetchPromise = fetch(`${BASE_URL}/api/experimental-users/email/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers,
      });

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (response.status === 404) {
        // User not found in experimental users - this is normal for regular users
        return null;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error checking experimental access:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const userData = await response.json();
    
      return userData;
    } catch (err) {
      console.error('Error fetching experimental user:', err);
      return null;
    }
  }, [auth.idToken]);

  // Check all experimental access for a user
  const checkAllAccess = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      const experimentalUser = await getExperimentalUserByEmail(email);
      
      if (experimentalUser) {
        // User is in experimental program
        const featureType = experimentalUser.feature_type;
        const featureTypes = [featureType];
        
        const newAccess: ExperimentalAccess = {
          isVenereUser: featureType === 'venere',
          isAdminUser: featureType === 'admin',
          hasAnyAccess: true,
          featureTypes,
          lastChecked: new Date(),
          userData: experimentalUser,
        };
        
        setAccess(newAccess);
        return newAccess;
      } else {
        // User is not in experimental program
        const newAccess: ExperimentalAccess = {
          isVenereUser: false,
          isAdminUser: false,
          hasAnyAccess: false,
          featureTypes: [],
          lastChecked: new Date(),
          userData: undefined,
        };
        
        setAccess(newAccess);
        return newAccess;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check experimental access';
      setError(errorMessage);
      
      // On error, assume user is regular (not experimental)
      const fallbackAccess: ExperimentalAccess = {
        isVenereUser: false,
        isAdminUser: false,
        hasAnyAccess: false,
        featureTypes: [],
        lastChecked: new Date(),
        userData: undefined,
      };
      
      setAccess(fallbackAccess);
      return fallbackAccess;
    } finally {
      setLoading(false);
    }
  }, [getExperimentalUserByEmail]);

  // Refresh access for current user
  const refreshAccess = useCallback(async () => {
    if (!userData?.email) {
      return access;
    }
    
    return await checkAllAccess(userData.email);
  }, [userData?.email, checkAllAccess]);

  // Check if user has access to a specific feature
  const hasAccess = useCallback((featureType: string): boolean => {
    return access.featureTypes.includes(featureType);
  }, [access.featureTypes]);

  // Check if user is an admin
  const isAdmin = useMemo((): boolean => {
    return access.isAdminUser;
  }, [access.isAdminUser]);

  // Check if user is a Venere user
  const isVenere = useMemo((): boolean => {
    return access.isVenereUser;
  }, [access.isVenereUser]);

  // Check if user has any experimental access
  const hasAnyAccess = useMemo((): boolean => {
    return access.hasAnyAccess;
  }, [access.hasAnyAccess]);

  // Get all feature types the user has access to
  const getFeatureTypes = useMemo((): string[] => {
    return access.featureTypes;
  }, [access.featureTypes]);

  // Get the experimental user data
  const getExperimentalUserData = useMemo((): ExperimentalUser | undefined => {
    return access.userData;
  }, [access.userData]);

  // Check if access is stale (older than 5 minutes)
  const isAccessStale = useMemo((): boolean => {
    if (!access.lastChecked) return true;
    
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
    
    return access.lastChecked < fiveMinutesAgo;
  }, [access.lastChecked]);

  // Auto-refresh access when user data changes
  useEffect(() => {
    
    if (userData?.email) {
      checkAllAccess(userData.email);
    } else {
      // Reset access when no user is logged in
      setAccess({
        isVenereUser: false,
        isAdminUser: false,
        hasAnyAccess: false,
        featureTypes: [],
        lastChecked: null,
        userData: undefined,
      });
    }
  }, [userData?.email, checkAllAccess]);

  // Auto-refresh stale access
  useEffect(() => {
    if (userData?.email && isAccessStale) {
      refreshAccess();
    }
  }, [userData?.email, isAccessStale, refreshAccess]);

  return {
    // State
    access,
    loading,
    error,
    
    // Computed values
    isAdmin,
    isVenere,
    hasAnyAccess,
    getFeatureTypes,
    getExperimentalUserData,
    isAccessStale,
    
    // Actions
    getExperimentalUserByEmail,
    checkAllAccess,
    refreshAccess,
    hasAccess,
    
    // Utilities
    clearError: () => setError(null),
  };
}; 